/**
 * Country / site selector. Fully client-side so it's safe behind static caching.
 * Cookie value = Craft site handle.
 *
 * Decision table:
 *   cookie == current            -> nothing
 *   cookie != current            -> banner (never an auto-redirect)
 *   no cookie, geo unmapped      -> nothing (UK is the fallback)
 *   no cookie, geo == current    -> set cookie (inferred)
 *   no cookie, geo != current    -> dialog with region dropdown
 */
export function countrySelector() {
  const COOKIE = 'site_pref';
  const DISMISS_KEY = 'site_pref_dismissed';
  const GEO_KEY = 'site_geo';
  const INFERRED_DAYS = 60;
  const EXPLICIT_DAYS = 365;
  const SET_COOKIE_ON_INFERENCE = true; // false if the client's consent setup says no
  const SAVE_CHECKED_BY_DEFAULT = false; // pre-ticked "save" box? (consent question)
  const SKIP_PARAMS = new Set(['geo']); // query params NOT carried to the target site

  // Draft copy (awaiting client sign-off)
  const copy = {
    heading: 'Looking for a different site?',
    body: (place, currentName) => (place
        ? `It looks like you're visiting from ${place}, but you're on our ${currentName} site. Choose your region to see the right products, pricing and information.`
        : `You're on our ${currentName} site. Choose your region to see the right products, pricing and information.`),
    select: 'Select your region',
    go: 'Go to selected site',
    stay: (currentName) => `Stay on ${currentName} site`,
    save: 'Save this region for future visits',
    close: 'Close',
    // Banner (returning visitor whose saved region differs from this site)
    bannerTitle: (currentName, targetName) => `You're viewing the ${currentName} site. Switch to ${targetName}?`,
    bannerGo: (targetName) => `Go to ${targetName}`,
    bannerStay: 'Stay here',
  };

  const dataEl = document.getElementById('country-selector-data');
  if (!dataEl) return;

  const cfg = JSON.parse(dataEl.textContent);
  const bySite = Object.fromEntries(cfg.sites.map((s) => [s.handle, s]));
  const current = bySite[cfg.current];
  if (!current) return;

  const store = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } },
  };

  const getCookie = () => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  };

  const setCookie = (value, days) => {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${days * 86400}; SameSite=Lax${secure}`;
  };

  // Carry the current query string and hash over to the target site.
  // Params already on the target URL win; SKIP_PARAMS are dropped.
  const withCurrentQuery = (url) => {
    const target = new URL(url, location.origin);
    const existing = new Set(target.searchParams.keys());

    new URLSearchParams(location.search).forEach((value, key) => {
      if (SKIP_PARAMS.has(key) || existing.has(key)) return;
      target.searchParams.append(key, value);
    });

    if (location.hash) target.hash = location.hash;
    return target.toString();
  };

  const countryName = (code) => {
    if (!code) return null;
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || null;
    } catch {
      return null;
    }
  };

  const h = (tag, props = {}, ...kids) => {
    const node = document.createElement(tag);
    Object.entries(props).forEach(([k, v]) => {
      if (k === 'onclick') node.onclick = v;
      else node.setAttribute(k, v);
    });
    kids.flat().forEach((kid) => node.append(kid));
    return node;
  };

  // Resolves to { site: handle|null, country: 'US'|null } or null on failure
  async function detectGeo() {
    const override = new URLSearchParams(location.search).get('geo'); // dev mode only

    if (!override) {
      try {
        const cached = JSON.parse(store.get(GEO_KEY));
        if (cached && typeof cached === 'object') return cached;
      } catch { /* not cached */ }
    }

    const url = new URL(cfg.endpoint, location.origin);
    if (override) url.searchParams.set('country', override);

    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) return null;

    const { geoSite, country } = await res.json();
    const geo = { site: geoSite || null, country: country || null };
    if (!override) store.set(GEO_KEY, JSON.stringify(geo));
    return geo;
  }

  function open(mode, bannerTarget = null, place = null) {
    document.querySelector('.country-selector')?.remove();

    let chosen = false;

    const dlg = h('dialog', {
      class: `country-selector country-selector--${mode}`,
      'aria-labelledby': 'country-selector-title',
    });

    // save = true: remember the choice in the cookie.
    // save = false: go without remembering, and stop prompting for this session
    // (otherwise e.g. an FR visitor picking US would be asked again on the US site).
    const go = (handle, save) => {
      chosen = true;
      if (save) setCookie(handle, EXPLICIT_DAYS);
      else store.set(DISMISS_KEY, '1');

      if (handle === current.handle) dlg.close();
      else location.assign(withCurrentQuery(bySite[handle].url));
    };

    const btn = (label, onclick, primary = false) => h('button', {
      type: 'button',
      class: `country-selector__btn${primary ? ' country-selector__btn--primary' : ''}`,
      onclick,
    }, label);

    const closeBtn = h('button', {
      type: 'button',
      class: 'country-selector__close',
      'aria-label': copy.close,
      onclick: () => dlg.close(),
    }, '×');

    const title = (text) => h('h2', { id: 'country-selector-title', class: 'country-selector__title' }, text);

    if (mode === 'banner') {
      const target = bySite[bannerTarget];
      dlg.append(
          closeBtn,
          title(copy.bannerTitle(current.name, target.name)),
          h(
              'div',
              { class: 'country-selector__actions' },
              btn(copy.bannerGo(target.name), () => go(target.handle, true), true),
              btn(copy.bannerStay, () => dlg.close()),
          ),
      );
    } else {
      const save = h('input', { type: 'checkbox', id: 'country-selector-save', class: 'country-selector__checkbox' });
      save.checked = SAVE_CHECKED_BY_DEFAULT;

      const select = h(
          'select',
          { class: 'country-selector__select', 'aria-label': copy.select, autofocus: '' },
          h('option', { value: '', disabled: '', selected: '' }, copy.select),
          cfg.sites
              .filter((s) => s.handle !== current.handle)
              .map((s) => h('option', { value: s.handle }, s.name)),
      );

      const goBtn = btn(copy.go, () => go(select.value, save.checked), true);
      goBtn.disabled = true;
      select.addEventListener('change', () => { goBtn.disabled = !select.value; });

      dlg.append(
          closeBtn,
          title(copy.heading),
          h('p', { class: 'country-selector__body' }, copy.body(place, current.name)),
          h('div', { class: 'country-selector__row' }, select, goBtn),
          h('label', { class: 'country-selector__save', for: 'country-selector-save' }, save, copy.save),
          h('button', {
            type: 'button',
            class: 'country-selector__link',
            onclick: () => go(current.handle, save.checked),
          }, copy.stay(current.name)),
      );
    }

    dlg.addEventListener('close', () => {
      if (!chosen) store.set(DISMISS_KEY, '1'); // closed without choosing: don't nag this session
      dlg.remove();
    });

    document.body.append(dlg);
    if (mode === 'banner') dlg.show();
    else dlg.showModal();
  }

  // Optional footer link: <a href="#" data-country-selector-open>Change region</a>
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-country-selector-open]')) {
      e.preventDefault();
      open('dialog');
    }
  });

  const pref = getCookie();
  const dismissed = store.get(DISMISS_KEY) === '1';

  if (pref && bySite[pref]) {
    if (pref !== current.handle && !dismissed) open('banner', pref);
    return;
  }

  if (dismissed) return;

  detectGeo()
      .then((geo) => {
        if (!geo?.site || !bySite[geo.site]) return; // unmapped: UK fallback, no prompt, no cookie
        if (geo.site === current.handle) {
          if (SET_COOKIE_ON_INFERENCE) setCookie(geo.site, INFERRED_DAYS);
          return;
        }
        open('dialog', null, countryName(geo.country));
      })
      .catch(() => { /* fail quietly */ });
}
